"use client";

// Sprint 3 — Mejoras al Booking Wizard público
import { addMonths } from "date-fns";
import { css, cx } from "styled-system/css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { GsapCollapsibleSummary, GsapStepEnter } from "@/components/ui/gsap-ui";
import { useLocation } from "react-router-dom";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { es } from "date-fns/locale";
import ReCAPTCHA from "react-google-recaptcha";
import { CalendarDays, Check, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { BookCalendarGrid } from "@/components/book/book-calendar-grid";
import { GOOGLE_EVENT_COLORS, googleEventColorHex } from "@/lib/google-calendar-colors";
import { previewDepositAmount } from "@/lib/deposit-preview";
import { clearRememberedClient, loadRememberedClient, saveRememberedClient } from "@/lib/public-booking-storage";
import { WaitlistSignupForm } from "@/components/book/waitlist-signup-form";

type Service = {
  id: string;
  kind?: "service" | "court";
  name: string;
  description: string | null;
  duration: number;
  price: number | null;
  requiresDeposit: boolean;
  depositAmount: number | null;
  depositType: string | null;
  depositCurrency: string | null;
  /** Solo presente si el servicio viene de un programa de tratamiento activo del paciente. */
  sessionsRemaining?: number;
};

export type InitialRescheduleState = {
  appointmentId: string;
  cancelToken: string;
  serviceId: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
};

type Props = {
  slug: string;
  primaryColor: string;
  category: string | null;
  modality: string;
  services: Service[];
  staff?: { id: string; name: string; googleColorId: string }[];
  timezone: string;
  flexibleSchedule?: boolean;
  cancellationPolicy?: { hoursBeforeCancel: number; penaltyType: string } | null;
  banner?: { payment?: string; cancelled?: string; msg?: string };
  initialReschedule?: InitialRescheduleState | null;
  /** Email identificado en el paso previo (gate de programas); precarga el campo de contacto. */
  prefillEmail?: string;
  /** Si la agenda tiene lista de espera activada, ofrece anotarse cuando no hay horarios libres. */
  waitlistEnabled?: boolean;
  /** Token de lista de espera vigente (?wl=) — si está presente, se reclama tras reservar con éxito. */
  waitlistToken?: string | null;
  /** Turno liberado que se está ofreciendo: fija servicio y horario, no se pueden cambiar. */
  waitlistLock?: { serviceId: string; startTimeIso: string } | null;
};

type Step = 1 | 2 | 3 | 4;

/** Superficie alineada al tema de perfil público (--pp-*); evita heredar texto claro sobre bg.card blanco del Card base. */
const ppSurface = css({
  bg: "var(--pp-card-bg, #ffffff)",
  color: "var(--pp-text, #0f172a)",
  borderColor: "var(--pp-border, #e2e8f0)",
});

/** Campos siempre legibles (fondo claro + texto oscuro) sobre cualquier --pp-card-bg */
const bookFormField = css({
  width: "100%",
  px: "3",
  py: "2",
  rounded: "md",
  borderWidth: "1px",
  borderColor: "var(--pp-border, #e2e8f0)",
  bg: "#ffffff",
  color: "#0f172a",
  fontSize: "sm",
  _placeholder: { color: "#64748b" },
  _focus: { outline: "none", borderColor: "token(colors.brand.500)", boxShadow: "0 0 0 1px token(colors.brand.500)" },
});

const bookSecondaryBtnStyle: CSSProperties = {
  backgroundColor: "color-mix(in srgb, var(--pp-border, #e2e8f0) 45%, var(--pp-card-bg, #ffffff))",
  color: "var(--pp-text, #0f172a)",
  border: "1px solid var(--pp-border, #e2e8f0)",
};

function modalityLabel(m: string): string {
  if (m === "virtual") return "Virtual";
  if (m === "combinada") return "Combinada";
  return "Presencial";
}

function serviceCardGradient(serviceId: string): string {
  let h = 0;
  for (let i = 0; i < serviceId.length; i++) h += serviceId.charCodeAt(i);
  const hue1 = (h * 47) % 360;
  const hue2 = (hue1 + 52) % 360;
  return `linear-gradient(135deg, hsl(${hue1} 78% 58%) 0%, hsl(${hue2} 72% 52%) 100%)`;
}

export function BookingWizard({
  slug,
  primaryColor,
  category: _category,
  modality,
  services,
  staff = [],
  timezone,
  flexibleSchedule = false,
  cancellationPolicy,
  banner,
  initialReschedule,
  prefillEmail,
  waitlistEnabled = false,
  waitlistToken,
  waitlistLock,
}: Props) {
  void _category;
  const location = useLocation();
  const rescheduleFromNav = (location.state as { reschedule?: InitialRescheduleState } | null)?.reschedule ?? null;
  const effectiveReschedule = initialReschedule ?? rescheduleFromNav ?? null;

  const [step, setStep] = useState<Step>(1);
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [dateStr, setDateStr] = useState<string>("");
  const [slots, setSlots] = useState<{ iso: string }[]>([]);
  const [slotIso, setSlotIso] = useState<string | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => formatInTimeZone(new Date(), timezone, "yyyy-MM"));
  const [availableDates, setAvailableDates] = useState<Set<string>>(new Set());
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [staffMemberId, setStaffMemberId] = useState<string>("");
  const [staffStatus, setStaffStatus] = useState<Record<string, boolean>>({});
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const recaptchaRef = useRef<InstanceType<typeof ReCAPTCHA>>(null);
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;

  useEffect(() => {
    if (staff.length === 1) setStaffMemberId(staff[0].id);
    else if (staff.length === 0) setStaffMemberId("");
  }, [staff]);

  useEffect(() => {
    const remembered = loadRememberedClient(slug);
    if (remembered) {
      setClientName(remembered.name);
      setClientEmail(remembered.email);
      setClientPhone(remembered.phone);
      setRememberMe(true);
    } else if (prefillEmail?.trim()) {
      setClientEmail(prefillEmail.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (!effectiveReschedule) return;
    setServiceId(effectiveReschedule.serviceId);
    setClientName(effectiveReschedule.clientName);
    setClientEmail(effectiveReschedule.clientEmail);
    setClientPhone(effectiveReschedule.clientPhone ?? "");
    setCalendarMonth(formatInTimeZone(new Date(), timezone, "yyyy-MM"));
    setDateStr("");
    setSlotIso(null);
    setSlots([]);
    setStep(2);
  }, [effectiveReschedule, timezone]);

  useEffect(() => {
    if (!waitlistLock) return;
    setServiceId(waitlistLock.serviceId);
    setSlotIso(waitlistLock.startTimeIso);
    setDateStr(formatInTimeZone(waitlistLock.startTimeIso, timezone, "yyyy-MM-dd"));
    setStep(3);
  }, [waitlistLock, timezone]);

  const selectedService = useMemo(
    () => services.find((s) => s.id === serviceId) ?? null,
    [services, serviceId],
  );

  const depositPreview = useMemo(
    () => (selectedService ? previewDepositAmount(selectedService) : null),
    [selectedService],
  );

  const policyLine = useMemo(() => {
    const h = cancellationPolicy?.hoursBeforeCancel ?? 24;
    if (h === 0) return "Podés cancelar sin límite de anticipación.";
    return `Podés cancelar hasta ${h} horas antes de tu turno.`;
  }, [cancellationPolicy]);

  const depositRefundPolicyLine = useMemo(() => {
    if (cancellationPolicy?.penaltyType === "lose_deposit") {
      return `⚠️ La seña es reembolsable si cancelás con más de ${cancellationPolicy.hoursBeforeCancel} horas de anticipación.`;
    }
    return "La seña es totalmente reembolsable si cancelás el turno.";
  }, [cancellationPolicy]);

  const minDate = useMemo(() => formatInTimeZone(new Date(), timezone, "yyyy-MM-dd"), [timezone]);
  const minCalendarMonth = minDate.slice(0, 7);
  const maxCalendarMonth = useMemo(
    () =>
      formatInTimeZone(addMonths(fromZonedTime(`${minCalendarMonth}-01T12:00:00`, timezone), 6), timezone, "yyyy-MM"),
    [timezone, minCalendarMonth],
  );

  function slotLabel(iso: string) {
    return formatInTimeZone(iso, timezone, "HH:mm");
  }

  const loadSlots = useCallback(async () => {
    if (!serviceId || !dateStr) return;
    setLoadingSlots(true);
    setError(null);
    try {
      const u = new URL("/api/public/availability", window.location.origin);
      u.searchParams.set("slug", slug);
      if (selectedService?.kind === "court") u.searchParams.set("courtId", serviceId);
      else u.searchParams.set("serviceId", serviceId);
      u.searchParams.set("date", dateStr);
      const res = await fetch(u.toString());
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Error");
      const raw = (data.slots ?? []) as { iso: string }[];
      setSlots(raw.map((s) => ({ iso: s.iso })));
      setSlotIso(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error");
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [slug, serviceId, dateStr, selectedService?.kind]);

  useEffect(() => {
    if (step !== 2 || !serviceId) return;
    let cancelled = false;
    setLoadingMonth(true);
    setError(null);
    const u = new URL("/api/public/availability-month", window.location.origin);
    u.searchParams.set("slug", slug);
    if (selectedService?.kind === "court") u.searchParams.set("courtId", serviceId);
    else u.searchParams.set("serviceId", serviceId);
    u.searchParams.set("month", calendarMonth);
    void fetch(u.toString())
      .then(async (res) => {
        const data = (await res.json().catch(() => ({}))) as { error?: string; dates?: string[] };
        if (!res.ok) throw new Error(data.error ?? "Error");
        return data.dates ?? [];
      })
      .then((dates) => {
        if (!cancelled) setAvailableDates(new Set(dates));
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Error");
          setAvailableDates(new Set());
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingMonth(false);
      });
    return () => {
      cancelled = true;
    };
  }, [step, serviceId, calendarMonth, slug, selectedService?.kind]);

  useEffect(() => {
    if (!dateStr) {
      setSlots([]);
      setSlotIso(null);
      return;
    }
    if (step !== 2 || !serviceId) return;
    void loadSlots();
  }, [dateStr, step, serviceId, loadSlots]);

  // Un mismo horario puede tener más de un atendente posible; consultamos quién está libre/ocupado
  // para ese instante puntual y se lo mostramos al elegir "Profesional" (ver /api/public/staff-availability).
  useEffect(() => {
    if (staff.length < 2 || !slotIso || !selectedService) {
      setStaffStatus({});
      return;
    }
    let cancelled = false;
    const u = new URL("/api/public/staff-availability", window.location.origin);
    u.searchParams.set("slug", slug);
    u.searchParams.set("startTimeIso", slotIso);
    u.searchParams.set("durationMinutes", String(selectedService.duration));
    void fetch(u.toString())
      .then((res) => (res.ok ? res.json() : { staff: [] }))
      .then((data: { staff?: { id: string; busy: boolean }[] }) => {
        if (cancelled) return;
        const map: Record<string, boolean> = {};
        for (const s of data.staff ?? []) map[s.id] = s.busy;
        setStaffStatus(map);
        setStaffMemberId((curr) => {
          if (curr && map[curr]) return "";
          if (!curr) {
            const freeOnes = staff.filter((s) => !map[s.id]);
            if (freeOnes.length === 1) return freeOnes[0].id;
          }
          return curr;
        });
      })
      .catch(() => {
        if (!cancelled) setStaffStatus({});
      });
    return () => {
      cancelled = true;
    };
  }, [staff, slotIso, selectedService, slug]);

  const goPrevMonth = useCallback(() => {
    setCalendarMonth((m) => {
      const prev = formatInTimeZone(addMonths(fromZonedTime(`${m}-01T12:00:00`, timezone), -1), timezone, "yyyy-MM");
      return prev < minCalendarMonth ? m : prev;
    });
  }, [timezone, minCalendarMonth]);

  const goNextMonth = useCallback(() => {
    setCalendarMonth((m) => {
      const next = formatInTimeZone(addMonths(fromZonedTime(`${m}-01T12:00:00`, timezone), 1), timezone, "yyyy-MM");
      return next > maxCalendarMonth ? m : next;
    });
  }, [timezone, maxCalendarMonth]);

  const canPrevMonth = calendarMonth > minCalendarMonth;
  const canNextMonth = calendarMonth < maxCalendarMonth;

  useEffect(() => {
    if (!dateStr) return;
    if (dateStr.slice(0, 7) !== calendarMonth) {
      setDateStr("");
      setSlotIso(null);
    }
  }, [calendarMonth, dateStr]);

  async function submitBooking() {
    if (!selectedService || !slotIso) return;
    setSubmitting(true);
    setError(null);
    try {
      let recaptchaToken: string | undefined;
      if (siteKey?.trim()) {
        const token = recaptchaRef.current?.getValue();
        if (!token) {
          setError("Completá el reCAPTCHA.");
          setSubmitting(false);
          return;
        }
        recaptchaToken = token;
      }

      const body: Record<string, unknown> =
        selectedService.kind === "court"
          ? {
              slug,
              courtId: selectedService.id,
              startTimeIso: slotIso,
              clientName,
              clientEmail,
              clientPhone: clientPhone || undefined,
            }
          : {
              slug,
              serviceId: selectedService.id,
              startTimeIso: slotIso,
              clientName,
              clientEmail,
              clientPhone: clientPhone || undefined,
            };
      if (recaptchaToken) body.recaptchaToken = recaptchaToken;
      if (staffMemberId) body.staffMemberId = staffMemberId;
      if (effectiveReschedule) {
        body.rescheduleFromAppointmentId = effectiveReschedule.appointmentId;
        body.rescheduleCancelToken = effectiveReschedule.cancelToken;
      }

      const res = await fetch("/api/public/bookings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "No se pudo reservar");

      if (waitlistToken) {
        try {
          await fetch("/api/public/waitlist/claim", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: waitlistToken }),
          });
        } catch {
          // No bloqueamos la confirmación de la reserva por un fallo al marcar el claim.
        }
      }

      if (rememberMe) {
        saveRememberedClient(slug, { name: clientName, email: clientEmail, phone: clientPhone });
      } else {
        clearRememberedClient(slug);
      }

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }

      const u = new URLSearchParams({ ref: String(data.appointmentId) });
      if (data.payOnSite) u.set("payOnSite", "1");
      window.location.href = `/book/${slug}/confirm?${u.toString()}`;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error");
      recaptchaRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  }

  async function submitRequest() {
    if (!selectedService) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        slug,
        ...(selectedService.kind === "court" ? { courtId: selectedService.id } : { serviceId: selectedService.id }),
        clientName,
        clientEmail: clientEmail || undefined,
        clientPhone: clientPhone || undefined,
        notes: notes || undefined,
      };
      const res = await fetch("/api/public/booking-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "No se pudo enviar la solicitud");
      if (rememberMe) {
        saveRememberedClient(slug, { name: clientName, email: clientEmail, phone: clientPhone });
      } else {
        clearRememberedClient(slug);
      }
      setRequestSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error");
    } finally {
      setSubmitting(false);
    }
  }

  const dateSummary = dateStr
    ? formatInTimeZone(fromZonedTime(`${dateStr}T12:00:00`, timezone), timezone, "EEEE d MMMM yyyy", {
        locale: es,
      })
    : "";

  const timeSummary = slotIso ? slotLabel(slotIso) : "";

  const summaryRow = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "3",
    py: "3",
    borderBottomWidth: "1px",
    borderColor: "var(--pp-border, #e2e8f0)",
    _last: { borderBottomWidth: "0" },
  });

  const modificarBtn = css({
    fontSize: "sm",
    fontWeight: "semibold",
    bg: "transparent",
    border: "none",
    cursor: "pointer",
    flexShrink: 0,
    textDecoration: "underline",
  });

  const contentShell = css({
    w: "100%",
    maxW: "100%",
    mx: "auto",
    px: "0",
  });

  if (requestSent) {
    return (
      <div className={css({ pb: "8" })}>
        <main className={cx(contentShell, css({ pb: "8", pt: { base: "4", lg: "6" } }))}>
          <Card
            className={css({
              boxShadow: "0 8px 32px rgba(15, 23, 42, 0.08)",
              borderWidth: "1px",
              borderColor: "var(--pp-border, token(colors.gray.200))",
              bg: "var(--pp-card-bg, white)",
              color: "var(--pp-text, #0f172a)",
              rounded: "xl",
              p: "8",
              textAlign: "center",
            })}
          >
            <div className={css({ display: "flex", justifyContent: "center", mb: "4" })}>
              <Check size={48} style={{ color: primaryColor }} aria-hidden />
            </div>
            <h2 className={css({ fontSize: "xl", fontWeight: "bold", mb: "3" })} style={{ color: "var(--pp-text, inherit)" }}>
              ¡Solicitud enviada!
            </h2>
            <p className={css({ fontSize: "sm", lineHeight: "1.6" })} style={{ color: "var(--pp-muted, #64748b)" }}>
              Tu solicitud de turno fue recibida. El profesional va a ponerse en contacto con vos para coordinar la fecha y hora.
            </p>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className={css({ pb: "8" })}>
      <main className={cx(contentShell, css({ pb: "8", pt: { base: "4", lg: "6" } }))}>
        {banner?.cancelled ? (
          <p className={css({ mb: "4", fontSize: "sm", color: "green.700" })}>Tu turno fue cancelado.</p>
        ) : null}
        {banner?.payment === "failed" ? (
          <p className={css({ mb: "4", fontSize: "sm", color: "red.600" })}>
            El pago no se completó. Podés intentar de nuevo.
          </p>
        ) : null}

        {effectiveReschedule ? (
          <p className={css({ mb: "4", fontSize: "sm", p: "3", rounded: "lg", bg: "brand.50", color: "accent.DEFAULT" })}>
            Estás reprogramando un turno. Elegí nueva fecha y horario; el turno anterior se cancela al confirmar.
          </p>
        ) : null}

        {flexibleSchedule && (
          <div
            className={css({
              mb: "4",
              p: "3",
              rounded: "lg",
              borderWidth: "1px",
              fontSize: "sm",
              lineHeight: "1.5",
            })}
            style={{
              backgroundColor: "color-mix(in srgb, var(--pp-border, #e2e8f0) 30%, var(--pp-card-bg, #fff))",
              borderColor: "var(--pp-border, #e2e8f0)",
              color: "var(--pp-text, #0f172a)",
            }}
          >
            Este profesional trabaja con turnos por solicitud. Completá tus datos y nos ponemos en contacto para coordinar.
          </div>
        )}

        <div className={css({ display: "flex", gap: "2", mb: "6", flexWrap: "wrap" })}>
          {flexibleSchedule ? (
            <>
              <Badge tone={step >= 1 ? "success" : "neutral"}>Servicio</Badge>
              <Badge tone={step >= 3 ? "success" : "neutral"}>Tus datos</Badge>
              <Badge tone={step >= 4 ? "success" : "neutral"}>Confirmar</Badge>
            </>
          ) : (
            <>
              <Badge tone={step >= 1 ? "success" : "neutral"}>Servicio</Badge>
              <Badge tone={step >= 2 ? "success" : "neutral"}>Fecha y hora</Badge>
              <Badge tone={step >= 3 ? "success" : "neutral"}>Tus datos</Badge>
              <Badge tone={step >= 4 ? "success" : "neutral"}>Confirmar</Badge>
            </>
          )}
        </div>

        <GsapCollapsibleSummary open={step >= 3} className={css({ mb: "4" })}>
              <Card className={cx(css({ p: "4" }), ppSurface)}>
                {dateStr ? (
                  <div className={summaryRow}>
                    <div className={css({ display: "flex", alignItems: "center", gap: "2", minW: 0 })}>
                      <Check size={18} className={css({ color: "green.600", flexShrink: 0 })} aria-hidden />
                      <span className={css({ fontSize: "sm" })}>
                        <strong>Fecha:</strong> {dateSummary}
                      </span>
                    </div>
                    <button type="button" className={modificarBtn} style={{ color: primaryColor }} onClick={() => setStep(2)}>
                      Modificar
                    </button>
                  </div>
                ) : null}
                {slotIso ? (
                  <div className={summaryRow}>
                    <div className={css({ display: "flex", alignItems: "center", gap: "2", minW: 0 })}>
                      <Check size={18} className={css({ color: "green.600", flexShrink: 0 })} aria-hidden />
                      <span className={css({ fontSize: "sm" })}>
                        <strong>Hora:</strong> {timeSummary}
                      </span>
                    </div>
                    <button type="button" className={modificarBtn} style={{ color: primaryColor }} onClick={() => setStep(2)}>
                      Modificar
                    </button>
                  </div>
                ) : null}
                {step === 4 && clientName ? (
                  <div className={summaryRow}>
                    <div className={css({ display: "flex", alignItems: "center", gap: "2", minW: 0 })}>
                      <Check size={18} className={css({ color: "green.600", flexShrink: 0 })} aria-hidden />
                      <span className={css({ fontSize: "sm", truncate: true })}>
                        <strong>Contacto:</strong> {clientName} — {clientEmail}
                      </span>
                    </div>
                    <button type="button" className={modificarBtn} style={{ color: primaryColor }} onClick={() => setStep(3)}>
                      Modificar
                    </button>
                  </div>
                ) : null}
              </Card>
        </GsapCollapsibleSummary>

        <Card
          className={css({
            boxShadow: "0 8px 32px rgba(15, 23, 42, 0.08)",
            borderWidth: "1px",
            borderColor: "var(--pp-border, token(colors.gray.200))",
            bg: "var(--pp-card-bg, white)",
            color: "var(--pp-text, #0f172a)",
            rounded: "xl",
          })}
        >
          {error ? (
            <p className={css({ color: "red.600", fontSize: "sm", mb: "4" })}>{error}</p>
          ) : null}
          <GsapStepEnter key={`step-${step}`}>
              {step === 1 && (
                <div className={css({ display: "flex", flexDir: "column", gap: "5" })}>
                  <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
                    <CalendarDays size={24} strokeWidth={2} style={{ color: primaryColor }} aria-hidden />
                    <h2
                      className={css({ fontSize: { base: "lg", lg: "xl" }, fontWeight: "bold", letterSpacing: "-0.02em" })}
                      style={{ color: "var(--pp-text, inherit)" }}
                    >
                      Agendar reserva
                    </h2>
                  </div>
                  {services.length === 0 ? (
                    <p className={css({ fontSize: "sm" })} style={{ color: "var(--pp-muted, inherit)" }}>
                      Este profesional aún no cargó servicios.
                    </p>
                  ) : (
                    <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
                      {services.map((s) => (
                        <div
                          key={s.id}
                          className={css({
                            display: "flex",
                            flexDir: { base: "column", sm: "row" },
                            alignItems: { sm: "center" },
                            gap: "4",
                            p: "5",
                            rounded: "xl",
                            borderWidth: "1px",
                            borderColor: "var(--pp-border, token(colors.gray.200))",
                            bg: "var(--pp-card-bg, white)",
                            boxShadow: "0 4px 20px rgba(15, 23, 42, 0.05)",
                          })}
                        >
                          <div
                            className={css({ w: "14", h: "14", rounded: "full", flexShrink: 0 })}
                            style={{ background: serviceCardGradient(s.id) }}
                            aria-hidden
                          />
                          <div className={css({ flex: "1", minW: 0 })}>
                            <div className={css({ display: "flex", alignItems: "center", gap: "2", flexWrap: "wrap" })}>
                              <p className={css({ fontWeight: "bold", fontSize: "md" })} style={{ color: "var(--pp-text, inherit)" }}>
                                {s.name}
                              </p>
                              {s.sessionsRemaining != null ? (
                                <span
                                  className={css({
                                    fontSize: "11px",
                                    fontWeight: "bold",
                                    px: "2",
                                    py: "0.5",
                                    rounded: "full",
                                    bg: "#ede9fe",
                                    color: "#6d28d9",
                                    whiteSpace: "nowrap",
                                  })}
                                >
                                  {s.sessionsRemaining} {s.sessionsRemaining === 1 ? "restante" : "restantes"}
                                </span>
                              ) : null}
                            </div>
                            <p className={css({ fontSize: "sm", mt: "1", lineHeight: "1.45" })} style={{ color: "var(--pp-muted, inherit)" }}>
                              {s.description?.trim() || "Reservá tu turno en unos pasos."}
                            </p>
                            <div
                              className={css({
                                display: "flex",
                                alignItems: "center",
                                gap: "2",
                                mt: "3",
                                fontSize: "xs",
                              })}
                              style={{ color: "var(--pp-muted, inherit)" }}
                            >
                              <span className={css({ w: "2", h: "2", rounded: "full", bg: "green.500", flexShrink: 0 })} />
                              {flexibleSchedule ? "Solicitá tu turno y coordinamos la fecha" : "Elegí fecha y horario en el siguiente paso"}
                            </div>
                            <p className={css({ fontSize: "xs", mt: "2" })} style={{ color: "var(--pp-muted, inherit)" }}>
                              {s.duration} min
                              {s.price != null ? ` · $${s.price}` : ""}
                              {s.requiresDeposit && previewDepositAmount(s) != null
                                ? ` · Seña: $${previewDepositAmount(s)}`
                                : null}
                            </p>
                          </div>
                          <div
                            className={css({
                              display: "flex",
                              flexDir: "column",
                              alignItems: { base: "stretch", sm: "flex-end" },
                              gap: "3",
                              flexShrink: 0,
                              w: { base: "full", sm: "auto" },
                            })}
                          >
                            <span
                              className={css({
                                alignSelf: { base: "flex-start", sm: "flex-end" },
                                fontSize: "xs",
                                fontWeight: "semibold",
                                px: "3",
                                py: "1",
                                rounded: "full",
                                borderWidth: "1px",
                              })}
                              style={{
                                color: "var(--pp-muted, inherit)",
                                borderColor: "var(--pp-border, #e2e8f0)",
                                backgroundColor: "color-mix(in srgb, var(--pp-page-bg, #f8fafc) 80%, white)",
                              }}
                            >
                              {modalityLabel(modality)}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setServiceId(s.id);
                                setCalendarMonth(formatInTimeZone(new Date(), timezone, "yyyy-MM"));
                                setDateStr("");
                                setSlotIso(null);
                                setSlots([]);
                                setStep(flexibleSchedule ? 3 : 2);
                              }}
                              className={css({
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "1",
                                px: "5",
                                py: "3",
                                rounded: "xl",
                                fontSize: "sm",
                                fontWeight: "semibold",
                                color: "white",
                                border: "none",
                                cursor: "pointer",
                                w: { base: "full", sm: "auto" },
                                transition: "opacity 0.15s, transform 0.15s",
                                _hover: { opacity: 0.92 },
                              })}
                              style={{ backgroundColor: primaryColor }}
                            >
                              {flexibleSchedule ? "Solicitar turno" : "Reservar"}
                              <ChevronRight size={18} aria-hidden />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {step === 2 && (
                <div className={css({ display: "flex", flexDir: "column", gap: "5" })}>
                  <div>
                    <h2
                      className={css({ fontSize: { base: "lg", lg: "xl" }, fontWeight: "bold" })}
                      style={{ color: "var(--pp-text, inherit)" }}
                    >
                      Elegí fecha y horario
                    </h2>
                    <p className={css({ fontSize: "xs", mt: "2" })} style={{ color: "var(--pp-muted, inherit)" }}>
                      Solo podés elegir días con turnos libres según la agenda del profesional.
                    </p>
                  </div>

                  <div
                    className={css({
                      display: "flex",
                      flexDir: { base: "column", lg: "row" },
                      gap: { base: "6", lg: "8" },
                      alignItems: { lg: "flex-start" },
                    })}
                  >
                    <div className={css({ w: { base: "100%", lg: "auto" } })}>
                      <BookCalendarGrid
                        calendarMonth={calendarMonth}
                        onPrevMonth={goPrevMonth}
                        onNextMonth={goNextMonth}
                        canPrevMonth={canPrevMonth}
                        canNextMonth={canNextMonth}
                        timeZone={timezone}
                        primaryColor={primaryColor}
                        minDateStr={minDate}
                        availableDates={availableDates}
                        selectedDate={dateStr}
                        onSelectDate={(d) => {
                          setDateStr(d);
                          setSlotIso(null);
                        }}
                        loadingMonth={loadingMonth}
                      />
                      {!loadingMonth && availableDates.size === 0 ? (
                        <p className={css({ fontSize: "xs", mt: "3", lineHeight: "1.45" })} style={{ color: "var(--pp-muted)" }}>
                          No hay días con turnos libres en este mes. Probá otro mes con las flechas.
                        </p>
                      ) : null}
                    </div>

                    <div className={css({ flex: "1", minW: 0, w: "100%" })}>
                      <p
                        className={css({ fontWeight: "semibold", mb: "3", fontSize: "sm" })}
                        style={{ color: "var(--pp-text, inherit)" }}
                      >
                        Horarios disponibles
                      </p>
                      {!dateStr ? (
                        <p className={css({ fontSize: "sm" })} style={{ color: "var(--pp-muted, inherit)" }}>
                          Tocá un día resaltado en el calendario para ver los horarios.
                        </p>
                      ) : loadingSlots ? (
                        <LoadingFrame variant="light" compact minHeight={120} />
                      ) : slots.length === 0 ? (
                        <p className={css({ fontSize: "sm" })} style={{ color: "var(--pp-muted, inherit)" }}>
                          No hay horarios para esa fecha. Probá otro día.
                        </p>
                      ) : (
                        <div
                          className={css({
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(88px, 1fr))",
                            gap: "2",
                          })}
                        >
                          {slots.map((s) => (
                            <button
                              key={s.iso}
                              type="button"
                              onClick={() => setSlotIso(s.iso)}
                              className={css({
                                py: "2.5",
                                px: "2",
                                rounded: "lg",
                                borderWidth: "2px",
                                borderColor: slotIso === s.iso ? primaryColor : "var(--pp-border, #e2e8f0)",
                                bg: slotIso === s.iso ? "color-mix(in srgb, var(--pp-page-bg) 60%, white)" : "var(--pp-card-bg, white)",
                                cursor: "pointer",
                                fontSize: "sm",
                                fontWeight: "semibold",
                              })}
                              style={{
                                color: slotIso === s.iso ? primaryColor : "var(--pp-text, inherit)",
                              }}
                            >
                              {formatInTimeZone(s.iso, timezone, "HH:mm")}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {waitlistEnabled && !loadingMonth && !loadingSlots && (availableDates.size === 0 || (dateStr !== "" && slots.length === 0)) ? (
                    <WaitlistSignupForm
                      slug={slug}
                      primaryColor={primaryColor}
                      services={services.filter((s) => s.kind !== "court").map((s) => ({ id: s.id, name: s.name }))}
                    />
                  ) : null}

                  <div className={css({ display: "flex", gap: "2", flexWrap: "wrap", pt: "2" })}>
                    <Button type="button" variant="secondary" style={bookSecondaryBtnStyle} onClick={() => setStep(1)}>
                      Atrás
                    </Button>
                    <Button type="button" variant="primary" disabled={!slotIso} onClick={() => setStep(3)}>
                      Continuar
                    </Button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
                  <h2 className={css({ fontSize: "lg", fontWeight: "semibold", color: "var(--pp-text, inherit)" })}>Tus datos</h2>
                  {waitlistLock && selectedService ? (
                    <div
                      className={css({
                        rounded: "lg",
                        borderWidth: "1px",
                        borderColor: "green.200",
                        bg: "green.50",
                        p: "3",
                        fontSize: "sm",
                        color: "green.900",
                      })}
                    >
                      Reservando <strong>{selectedService.name}</strong> · {dateSummary} · {timeSummary}
                    </div>
                  ) : null}
                  <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                    <span className={css({ fontSize: "sm", color: "var(--pp-muted, #64748b)" })}>Nombre</span>
                    <Input className={bookFormField} value={clientName} onChange={(e) => setClientName(e.target.value)} required />
                  </label>
                  {staff.length > 1 ? (
                    <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                      <span className={css({ fontSize: "sm", color: "var(--pp-muted, #64748b)" })}>Profesional</span>
                      <select
                        className={bookFormField}
                        value={staffMemberId}
                        onChange={(e) => setStaffMemberId(e.target.value)}
                        required
                      >
                        <option value="">Seleccioná quién te atiende</option>
                        {staff.map((s) => (
                          <option key={s.id} value={s.id} disabled={staffStatus[s.id]}>
                            {s.name}
                            {staffStatus[s.id] ? " (Ocupada)" : ""}
                          </option>
                        ))}
                      </select>
                      {staff.length > 0 &&
                      Object.keys(staffStatus).length > 0 &&
                      staff.every((s) => staffStatus[s.id]) ? (
                        <span className={css({ fontSize: "xs", color: "#b91c1c" })}>
                          Todos los profesionales están ocupados a esa hora. Elegí otro horario.
                        </span>
                      ) : null}
                      {staffMemberId ? (
                        <span className={css({ display: "inline-flex", alignItems: "center", gap: "2", fontSize: "xs", color: "var(--pp-muted)" })}>
                          <span
                            className={css({ w: "3", h: "3", rounded: "full" })}
                            style={{ background: googleEventColorHex(staff.find((s) => s.id === staffMemberId)?.googleColorId) }}
                          />
                          Color en Google Calendar
                        </span>
                      ) : null}
                    </label>
                  ) : null}
                  <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                    <span className={css({ fontSize: "sm", color: "var(--pp-muted, #64748b)" })}>
                      Email
                      {!clientPhone.trim() && <span style={{ color: "#b91c1c" }}> *</span>}
                    </span>
                    <Input
                      type="email"
                      className={bookFormField}
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      placeholder="tu@email.com"
                    />
                  </label>
                  <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                    <span className={css({ fontSize: "sm", color: "var(--pp-muted, #64748b)" })}>
                      Teléfono WhatsApp
                      {!clientEmail.trim() && <span style={{ color: "#b91c1c" }}> *</span>}
                    </span>
                    <Input
                      type="tel"
                      className={bookFormField}
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      placeholder="Ej: 3794556599"
                    />
                  </label>
                  <p className={css({ fontSize: "xs", color: "var(--pp-muted, #64748b)", lineHeight: "1.5", mt: "-1" })}>
                    Completá al menos uno: email o teléfono.
                  </p>
                  {flexibleSchedule && (
                    <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                      <span className={css({ fontSize: "sm", color: "var(--pp-muted, #64748b)" })}>Notas u observaciones (opcional)</span>
                      <textarea
                        className={bookFormField}
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Indicá días u horarios preferidos, o cualquier detalle relevante"
                        style={{ resize: "vertical" }}
                      />
                    </label>
                  )}
                  <label
                    className={css({
                      display: "flex",
                      alignItems: "center",
                      gap: "2",
                      fontSize: "sm",
                      cursor: "pointer",
                      color: "var(--pp-text, inherit)",
                    })}
                  >
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    Recordar mis datos para la próxima vez
                  </label>
                  <div className={css({ display: "flex", gap: "2" })}>
                    {!waitlistLock ? (
                      <Button type="button" variant="secondary" style={bookSecondaryBtnStyle} onClick={() => setStep(flexibleSchedule ? 1 : 2)}>
                        Atrás
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="primary"
                      disabled={
                        !clientName ||
                        (!clientEmail.trim() && !clientPhone.trim()) ||
                        (staff.length > 1 && !staffMemberId)
                      }
                      onClick={() => setStep(4)}
                    >
                      Continuar
                    </Button>
                  </div>
                </div>
              )}

              {step === 4 && selectedService && (
                <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
                  <h2 className={css({ fontSize: "lg", fontWeight: "semibold", color: "var(--pp-text, inherit)" })}>
                    {flexibleSchedule ? "Confirmá tu solicitud" : "Confirmación"}
                  </h2>
                  <ul className={css({ fontSize: "sm", color: "var(--pp-muted, #64748b)", mb: "2" })}>
                    <li>{selectedService.name}</li>
                    {!flexibleSchedule && <li>{dateSummary}</li>}
                    {!flexibleSchedule && <li>{timeSummary}</li>}
                    <li>
                      {clientName}{clientEmail ? ` — ${clientEmail}` : ""}{clientPhone ? ` · ${clientPhone}` : ""}
                    </li>
                    {flexibleSchedule && notes && <li style={{ marginTop: "4px", fontStyle: "italic" }}>{notes}</li>}
                  </ul>
                  {flexibleSchedule ? (
                    <p className={css({ fontSize: "xs", color: "var(--pp-muted, #64748b)", lineHeight: "1.5" })}>
                      Al confirmar tu solicitud le vamos a avisar al profesional para que se ponga en contacto con vos.
                    </p>
                  ) : (
                    <p className={css({ fontSize: "xs", color: "var(--pp-muted, #64748b)", lineHeight: "snug" })}>{policyLine}</p>
                  )}
                  {!flexibleSchedule && depositPreview != null && depositPreview > 0 ? (
                    <div
                      className={css({
                        rounded: "lg",
                        borderWidth: "1px",
                        borderColor: "amber.200",
                        bg: "amber.50",
                        p: "4",
                      })}
                    >
                      <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "amber.950", mb: "1" })}>
                        Seña requerida
                      </p>
                      <p className={css({ fontSize: "sm", color: "amber.900" })}>
                        Para confirmar la reserva necesitás pagar una seña de{" "}
                        <strong>
                          {(selectedService.depositCurrency ?? "ARS") === "ARS" ? "$" : `${selectedService.depositCurrency} `}
                          {depositPreview}
                        </strong>
                        . Te vamos a redirigir a Mercado Pago.
                      </p>
                      <p className={css({ fontSize: "xs", color: "amber.900", mt: "2", lineHeight: "1.5" })}>
                        {depositRefundPolicyLine}
                      </p>
                    </div>
                  ) : null}
                  {!flexibleSchedule && siteKey?.trim() ? (
                    <div className={css({ display: "flex", justifyContent: "center", my: "2" })}>
                      <ReCAPTCHA ref={recaptchaRef} sitekey={siteKey.trim()} />
                    </div>
                  ) : null}
                  {error ? (
                    <p className={css({ color: "red.600", fontSize: "sm" })}>{error}</p>
                  ) : null}
                  <div className={css({ display: "flex", gap: "2" })}>
                    <Button type="button" variant="secondary" style={bookSecondaryBtnStyle} onClick={() => setStep(3)}>
                      Atrás
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={submitting}
                      onClick={() => void (flexibleSchedule ? submitRequest() : submitBooking())}
                    >
                      {submitting
                        ? "Procesando…"
                        : flexibleSchedule
                          ? "Enviar solicitud"
                          : depositPreview != null && depositPreview > 0
                            ? "Pagar seña con Mercado Pago"
                            : "Confirmar reserva"}
                    </Button>
                  </div>
                </div>
              )}
          </GsapStepEnter>
        </Card>

      </main>
    </div>
  );
}
